import { UIUtilFormat } from "../../../ui/util/Format";
import { UIUtilStrings } from "../../../ui/util/Strings";
import { UIUtilTime } from "../../../ui/util/Time";
import { Entidad } from "../../Entidad";
import { CFinanzaPagoEnLineaEstado, IFinanzaEdoCtaMovimiento, IFinanzaEdoCtaMovimientoDetalle, IFinanzaFolioPagoEnLinea } from "../../entidad/FinanzasEstadoCuenta";
import { ModelAdapter } from "../ModelAdapter";
import { MFinanzaEdoCtaMovimientoDetalle } from "./MFinanzaEdoCtaMovimientoDetalle";

import CCategoriaMov = Entidad.CFinanzaCategoriaMovimiento;

export const ID_METODOPAGO_ENLINEA_TEMP = -1; // Openpay (temporal)

export class MFinanzaEdoCtaMovimiento extends ModelAdapter<IFinanzaEdoCtaMovimiento> implements IFinanzaEdoCtaMovimiento {
    Cargo: string;
    Categoria: Entidad.CFinanzaCategoriaMovimiento;
    CicloEscolar: number;
    // Detalles: MFinanzaEdoCtaMovimientoDetalle[]; // Usa encapsulado, valor final en this.__Detalles
    FechaAplicacion: string;
    FechaFin: string;
    FechaInicio: string;
    FechaMorosidad: string;
    FechaVencimiento: string;
    Id: number;
    IdAgrupador: number;
    IdAsignacionCargo: number;
    IdCargo: number;
    Periodicidad: Entidad.CFinanzaCargoPeriodicidadCargos;
    Recargo: number;
    Saldo: number;
    TipoInteres: Entidad.CFinanzaCargoTipoInteres;
    TipoValor: Entidad.CFinanzaCargoTipoValor;
    Valor: number;

    IdEscuela: number; // Auxiliar interno

    // =================================================
    // PROPIEDADES ADICIONALES
    // =================================================

    /** Detalles originales de la entidad */
    private __DetallesReales: IFinanzaEdoCtaMovimientoDetalle[];
    /** Movimientos válidos y transformados a la clase MFinanzaEdoCtaMovimientoDetalle */
    private __Detalles: MFinanzaEdoCtaMovimientoDetalle[];

    private __foliosPagoEnLineaAsignados?: IFinanzaFolioPagoEnLinea[] = [];
    private __detallesPagoOnlinePendientes?: MFinanzaEdoCtaMovimientoDetalle[] = [];

    constructor(data: IFinanzaEdoCtaMovimiento, idEscuela: number) {
        const detallesReales = data.Detalles;
        delete data.Detalles; // Para evitar settear la prop 'Detalles'
        super(data);
        this.__DetallesReales = detallesReales;
        this.IdEscuela = idEscuela;
        this.init();
    }

    // =================================================
    // GETTERS ADICIONALES
    // =================================================

    /** @override retorna los detalles válidos y transformados a la clase MFinanzaEdoCtaMovimientoDetalle
     *
     * * Se filtran detalles de morosidad con recargo 0, ya que no representan un movimiento real en el estado de cuenta
     * * Sobre los movimientos de tipo PagoCompleto o PagoParcial, solo contiene pagos en linea conciliados
     * y Pagos manuales
    */
    get Detalles(): MFinanzaEdoCtaMovimientoDetalle[] {
        return this.__Detalles;
    }

    // set Detalles(value: MFinanzaEdoCtaMovimientoDetalle[]) {
    //     this.__Detalles = value;
    // }

    /** Retorna todos los detalles válidos, incluyendo los pagos en línea pendientes */
    get DetallesYTransacciones(): MFinanzaEdoCtaMovimientoDetalle[] {
        return this.Detalles.concat(this.__detallesPagoOnlinePendientes);
    }

    get Periodo(): string {
        return this.GetStrPeriodo(this.FechaInicio, this.FechaFin);
    }

    get SaldoForTable(): string {
        if (this.DetallesYTransacciones?.length > 0) {
            return "";
        }
        return UIUtilFormat._CurrencyFmt(this.Saldo);
    }

    /** Solo para configuración de tabla (ui) */
    get PagoAbono(): string {
        return null;
    }
    /** Solo para configuración de tabla (ui) */
    get FechaPago(): string {
        return null;
    }
    /** Solo para configuración de tabla (ui) */
    get MetodoPago(): string {
        return null;
    }

    /** @override retorna un clon exacto de la entidad */
    get __Entity(): IFinanzaEdoCtaMovimiento {
        const entityBase = super.__Entity;
        return {
            ...entityBase,
            Detalles: this.__DetallesReales,
        }
    }

    // =================================================
    // METODOS PÚBLICOS
    // =================================================

    public _AgregarFoliosPagoEnLineaAsignados(folioPagoEnLinea: IFinanzaFolioPagoEnLinea[]): void {
        folioPagoEnLinea.forEach(folio => {
            if (this.__foliosPagoEnLineaAsignados?.some(f => f.Id == folio.Id)) {
                return;
            }
            this.__foliosPagoEnLineaAsignados.push(folio);
        });

        this.DistribuirFoliosPagoADetallesConciliados();
        this.CrearDetallesPagoOnlinePendientes();
    }

    /** Retorna true si algún detalle tiene un pago completo conciliado.
     *
     * Un pago completo se considera conciliado cuando tiene un método de pago asignado
     * o no tiene pagos en línea PENDIENTES relacionados.
     */
    public _TienePagoCompletoConciliado(): boolean {
        return this.Detalles.some(detalle => (CCategoriaMov.PagoCompleto == detalle.Categoria));
    }

    /** Retorna true si algún detalle o transacción (en curso) es PagoCompleto */
    public _TienePagoCompletoInvolucrado(): boolean {
        return this.DetallesYTransacciones.some(detalle => (CCategoriaMov.PagoCompleto == detalle.Categoria));
    }

    /** Retorna true si algún detalle o transacción (en curso) es PagoParcial o PagoCompleto */
    public _TienePagoInvolucrado(): boolean {
        const tipoPagoValidar = [CCategoriaMov.PagoParcial, CCategoriaMov.PagoCompleto];
        return this.DetallesYTransacciones.some(detalle => tipoPagoValidar.includes(detalle.Categoria));
    }

    public _ObtenerSaldoFinal(): number {
        return this.ObtenerSaldoFinal();
    }

    /** Retorna true si el movimiento está saldado.
     * Un movimiento se considera saldado cuando su saldo final es menor o igual a 0, es decir, no tiene adeudo pendiente.
     */
    public _IsSaldado(): boolean {
        return this._ObtenerSaldoFinal() <= 0;
    }

    /**
     * Retorna el adeudo de recargos pendientes por pagar.
     */
    public _CalcularAdeudoRecargoPendiente(): number {
        return this.CalcularAdeudoRecargoPendiente();
    }

    /** Retorna true si algún detalle tiene un pago en línea pendiente */
    public _TienePagoOnlinePendiente(): boolean {
        if (this.DetallesYTransacciones) {
            return this.DetallesYTransacciones.some(d => d._IsPagoOnlinePendiente());
        }
        return false;
    }

    /** Retorna true si el movimiento puede ser pagado.
     *
     * Un movimiento se considera pagable si no está saldado y
     * no tiene pagos en línea pendientes, es decir,
     * tiene adeudo pendiente que no está siendo cubierto por un pago en línea en proceso.
     */
    public _IsPagoValido(): boolean {
        return !this._IsSaldado() && !this._TienePagoOnlinePendiente();
    }

    // =================================================
    // PRIVATE METHODS
    // =================================================

    private init() {
        this.__Detalles = this.__DetallesReales
            // Se filtran detalles de morosidad con recargo 0, ya que no representan un movimiento real en el estado de cuenta
            .filter(detalle => !(detalle.Categoria === CCategoriaMov.Morosidad && detalle.Recargo == 0))
            .map(detalle => new MFinanzaEdoCtaMovimientoDetalle(detalle, this));
    }

    /** Retorna el saldo final del movimiento.
     * Se obtiene del último detalle válido (si existe)
     * o del saldo del movimiento en caso de no tener detalles válidos.
    */
    private ObtenerSaldoFinal(): number {
        const detallesConciliados = this.__Detalles;
        if (detallesConciliados.length == 0) {
            return this.Saldo;
        }
        return detallesConciliados[detallesConciliados.length - 1].Saldo;
    }

    /** Recargo pendiente por pagar
     *
     * Se calcula sumando los recargos de los detalles del movimiento que sean de categoría morosidad,
     * sin método de pago asignado y que no estén seguidos por un detalle de pago
     * parcial o completo (ya que ese detalle indicaría que el recargo fue pagado aunque no tenga método de pago asignado)
    */
    private CalcularAdeudoRecargoPendiente(): number {
        let totalRecargos = 0;
        if (this.__Detalles && this.__Detalles.length > 0) {
            this.__Detalles
                .filter((d, i) => (
                    d.Categoria == CCategoriaMov.Morosidad
                    && d.IdMetodoPago == 0
                    && (!this.__Detalles[i + 1] || ![CCategoriaMov.PagoParcial, CCategoriaMov.PagoCompleto].includes(this.__Detalles[i + 1].Categoria))
                ))
                .forEach(d => totalRecargos += d.Recargo);
        }
        return totalRecargos;
    }

    private GetStrPeriodo(fechaInicio: string, fechaFin: string): string {
        if (!fechaInicio) {
            return "";
        }
        const startMonthName = UIUtilTime._DateFormatStandar(fechaInicio, "MMM yyyy");
        const endMonthName = UIUtilTime._DateFormatStandar(fechaFin, "MMM yyyy");

        if (!endMonthName || startMonthName == endMonthName) {
            return UIUtilStrings._CapitaliceString(startMonthName);
        }

        return `${UIUtilStrings._CapitaliceString(startMonthName)} - ${UIUtilStrings._CapitaliceString(endMonthName)}`;
    }

    private DistribuirFoliosPagoADetallesConciliados(): void {
        const foliosPagoOnlineConsiliados = this.__foliosPagoEnLineaAsignados?.filter(folio => folio.EstadoPago == CFinanzaPagoEnLineaEstado.Pagado) || [];
        if (!foliosPagoOnlineConsiliados.length) {
            this.Detalles.forEach(detalle => {
                detalle._AsignarFolioPagoEnLinea(null);
            });
            return;
        }
        this.Detalles.forEach(detalle => {
            const folioPagoRelacionado = foliosPagoOnlineConsiliados
                .find(folio => folio.IdsMovimientos.includes(this.Id) && folio.Folio == detalle.FolioPago);
            detalle._AsignarFolioPagoEnLinea(folioPagoRelacionado);
        });
    }

    private CrearDetallesPagoOnlinePendientes(): void {
        if (!this.__foliosPagoEnLineaAsignados?.length) {
            this.__detallesPagoOnlinePendientes = [];
            return;
        }
        this.__detallesPagoOnlinePendientes = this.__foliosPagoEnLineaAsignados
            .filter(folioPagoObj => folioPagoObj.EstadoPago == CFinanzaPagoEnLineaEstado.Pendiente)
            .map<MFinanzaEdoCtaMovimientoDetalle>((folioPagoObj) => {
                const indexMov = folioPagoObj.IdsMovimientos.indexOf(this.Id);
                if (indexMov < 0) {
                    console.warn(`El folio de pago en línea con id ${folioPagoObj.Id} no corresponde al movimiento con id ${this.Id}`);
                    return null;
                }
                const movPagoOnlineTemporal = this.CrearMovDetallePagoNoConsolidado(folioPagoObj, indexMov);
                return movPagoOnlineTemporal;
            })
            .filter(detalle => detalle !== null);
    }

    private CrearMovDetallePagoNoConsolidado(folioPagoObj: IFinanzaFolioPagoEnLinea, i: number): MFinanzaEdoCtaMovimientoDetalle {
        const tipoPago = folioPagoObj.TiposPago[i];
        const monto = folioPagoObj.Montos[i];
        const movPagoOnlineTemporal: IFinanzaEdoCtaMovimientoDetalle = {
            Id: i * -1,
            Cargo: "",
            Valor: monto,
            Recargo: 0,
            Saldo: this.ObtenerSaldoFinal(), // El saldo inicial es el monto de pago, ya que aún no se ha aplicado
            Categoria: tipoPago as unknown as Entidad.CFinanzaCategoriaMovimiento,
            FechaAplicacion: "",
            FolioPago: folioPagoObj.Folio,
            FechaMorosidad: null,
            FechaRegistro: folioPagoObj.FechaCreacion,
            IdAsignacionCargo: null,
            IdMetodoPago: ID_METODOPAGO_ENLINEA_TEMP,
            IdCargo: null,
            IdFacturaFolio: 0,
            IdUsuarioCreo: null,
            TipoInteres: null,
            TipoValor: null,
            UUID: null,
            Periodicidad: null,
        };
        const movDetalle = new MFinanzaEdoCtaMovimientoDetalle(movPagoOnlineTemporal, this);

        movDetalle._AsignarFolioPagoEnLinea(folioPagoObj);

        return movDetalle;
    }
}
